import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/button';
import Toast from '@/components/Toast';
import ToastIcons from '@/components/Toast/Icons';
import type { NotificationItem } from '@/types/notification.types';
import { cn } from '@/utils/cn';

import Icons from '../Icons';

import ActionRequiredBadge from './ActionRequiredBadge';
import EmptyNotificationIcon, { EMPTY_NOTIFICATION_ICON_INDICES } from './EmptyNotificationIcon';
import NotificationBadge from './NotificationBadge';
import NotificationTitle from './NotificationTitle';

interface NotificationCenterProps {
  /** Vault in-progress — spinning toolbar icon (excludes UNIT→USDC swap). */
  hasPendingVaultAction: boolean;
  /** Swap needs user signing / Continue — attention badge instead of unread count. */
  hasUserActionRequired: boolean;
  count: number;
  notifications: NotificationItem[];
  markAllAsViewed: VoidFunction;
  clearAll: VoidFunction;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  hasPendingVaultAction,
  hasUserActionRequired,
  count,
  notifications,
  markAllAsViewed,
  clearAll,
}: NotificationCenterProps) => {
  const t = useTranslations('layout.notifications');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const hasNotifications = notifications.length > 0;

  const renderToolbarIcon = () => {
    // Priority: vault in-progress spinner > swap user-action badge > unread count
    if (hasPendingVaultAction) {
      return <ToastIcons.Progress width="20" height="20" className="animate-spin" />;
    }
    if (hasUserActionRequired) {
      return (
        <>
          <Icons.Bell />
          <ActionRequiredBadge isRoot={true} />
        </>
      );
    }
    return (
      <>
        <Icons.Bell />
        <NotificationBadge isRoot={true} count={count} />
      </>
    );
  };

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <Button aria-label={t('ariaLabel')} variant="secondary" className="relative h-[30px]! w-[30px]! lg:h-10! lg:w-10!">
          {renderToolbarIcon()}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={10} // same as in WalletConnect (8px in Figma)
          align="end"
          onKeyDownCapture={(event) => {
            // These are ordinary controls, so retain native Tab traversal instead of menu-item navigation.
            if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
              event.stopPropagation();
            }
          }}
          className={cn(
            'w-[calc(100vw-60px)] sm:w-[440px] bg-surface-primary border border-border-primary rounded-lg overflow-hidden',
            'shadow-[0_2px_6px_0_rgba(0,0,0,0.08),0_8px_26px_0_rgba(0,0,0,0.12)] z-50',
          )}
        >
          <NotificationTitle count={count} markAllAsViewed={markAllAsViewed} />
          <div className="flex flex-col h-[calc(100dvh-140px)] lg:max-h-[calc(100dvh-180px)]">
            <div className="min-h-0 flex-1 overflow-y-auto">
              {hasNotifications ? (
                notifications.map((item) => <Toast key={item.id} isNotificationCenterItem {...item} />)
              ) : (
                <div className="flex flex-col items-center justify-center gap-6 h-full">
                  <div>
                    {EMPTY_NOTIFICATION_ICON_INDICES.map((index) => (
                      <EmptyNotificationIcon key={index} index={index} />
                    ))}
                  </div>
                  <p className="text-sm text-text-secondary">{t('empty')}</p>
                </div>
              )}
            </div>
            {hasNotifications && (
              <div className="shrink-0 flex justify-center border-t border-border-primary bg-surface-primary p-3">
                <Button variant="transparent" size="small" leadingIcon={<Icons.Bin />} onClick={clearAll}>
                  {t('deleteAll')}
                </Button>
              </div>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default NotificationCenter;
